import '../../css/_apartments.css';

import { qs, ready } from '../utils/helpers';

type Bounds = {
  floor_min: number;
  floor_max: number;
  room_min: number;
  room_max: number;
  area_min: number;
  area_max: number;
};

type MigdolaiApartmentsCfg = {
  ajaxUrl: string;
  nonce: string;
  action: string;
  bounds: Bounds;
  /** When set (e.g. choose-house + ?komercines_patalpos=1), AJAX always filters to this patalpų tipas. */
  forcedPatalpuType?: string;
  /** SEO preset URL (e.g. /paieska/3-4-kambariu-butai/): initial rooms range; also scrolls to the quick search. */
  presetRooms?: [number, number] | null;
  i18n: Record<string, string>;
};

type ApartmentRow = {
  house: string;
  floor: number;
  room: string;
  area: string;
  balcony: string;
  apartment: string;
  status: string;
  features: string;
  link: string;
};

/** Minimal typing for vendor `assets/vendor/nouislider/nouislider.min.js` (from Samanų takai). */
type NoUiApi = {
  get(): string | string[];
  on(event: string, handler: () => void): void;
  destroy(): void;
};

type NoUiSliderGlobal = {
  create(target: HTMLElement, options: Record<string, unknown>): NoUiApi;
};

declare global {
  interface Window {
    noUiSlider?: NoUiSliderGlobal;
    migdolaiApartments?: MigdolaiApartmentsCfg;
  }
}

const APARTMENTS_TABLE_INNER =
  'apartments-table__inner mx-auto w-full max-w-[1720px] px-4 laptop:px-40 desktop:px-[240px]';

function debounce<T extends unknown[]>(fn: (...args: T) => void, ms: number): (...args: T) => void {
  let t: ReturnType<typeof setTimeout> | undefined;
  return (...args: T) => {
    if (t) {
      clearTimeout(t);
    }
    t = setTimeout(() => fn(...args), ms);
  };
}

function formatLtNumber(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') {
    return '—';
  }
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
  if (Number.isNaN(n)) {
    return String(raw);
  }
  return new Intl.NumberFormat('lt-LT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Plotas column: always two fraction digits (e.g. 45,00) for consistent table alignment. */
function formatLtAreaCell(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') {
    return '—';
  }
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
  if (Number.isNaN(n)) {
    return String(raw);
  }
  return new Intl.NumberFormat('lt-LT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function escapeHtml(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function statusBadgeClass(status: string): string {
  if (status === 'Laisvas' || status === 'Laisva') {
    return 'apartments-badge apartments-badge--free';
  }
  if (status === 'Rezervuotas') {
    return 'apartments-badge apartments-badge--reserved';
  }
  if (status === 'Parduotas') {
    return 'apartments-badge apartments-badge--sold';
  }
  return 'apartments-badge apartments-badge--sold';
}

function readCfg(): MigdolaiApartmentsCfg | null {
  const cfg = window.migdolaiApartments;
  if (!cfg?.ajaxUrl || !cfg.nonce) {
    return null;
  }
  return cfg;
}

function parsePair(api: NoUiApi | undefined): [number, number] {
  if (!api) {
    return [0, 0];
  }
  const raw = api.get();
  const arr = Array.isArray(raw) ? raw : [raw, raw];
  const a = Math.round(parseFloat(String(arr[0])));
  const b = Math.round(parseFloat(String(arr[1])));
  return [Number.isFinite(a) ? a : 0, Number.isFinite(b) ? b : 0];
}

function collectPayload(cfg: MigdolaiApartmentsCfg, sliders: { floor?: NoUiApi; room?: NoUiApi; area?: NoUiApi }): URLSearchParams {
  const forced = cfg.forcedPatalpuType === 'komercine' ? 'komercine' : '';
  const type = forced || (qs<HTMLSelectElement>('#apartments-type')?.value ?? '');
  const house = qs<HTMLSelectElement>('#apartments-house')?.value ?? '';
  const free = qs<HTMLInputElement>('#apartments-free')?.checked ? '1' : '0';

  const [f0, f1] = parsePair(sliders.floor);
  const [r0, r1] = parsePair(sliders.room);
  const [a0, a1] = parsePair(sliders.area);

  const body = new URLSearchParams();
  body.set('action', cfg.action);
  body.set('nonce', cfg.nonce);
  body.set('type', type);
  body.set('house', house);
  body.set('free', free);
  body.set('floors[0]', String(f0));
  body.set('floors[1]', String(f1));
  body.set('rooms[0]', String(r0));
  body.set('rooms[1]', String(r1));
  body.set('areas[0]', String(a0));
  body.set('areas[1]', String(a1));
  return body;
}

function rowAriaLabel(row: ApartmentRow): string {
  const parts = [row.house, row.apartment, row.status].filter(Boolean);
  return parts.join(', ');
}

function renderRows(cfg: MigdolaiApartmentsCfg, rows: ApartmentRow[]): void {
  const tbody = qs<HTMLElement>('#apartments-table-body');
  if (!tbody) {
    return;
  }

  if (rows.length === 0) {
    tbody.innerHTML = `
      <div class="apartments-table__row-container apartments-table__row-container--message apartments-table__empty">
        <div class="${APARTMENTS_TABLE_INNER}">
          <p class="apartments-table__row apartments-table__row--message">${escapeHtml(cfg.i18n.empty ?? '')}</p>
        </div>
      </div>`;
    return;
  }

  const frag = document.createDocumentFragment();
  for (const row of rows) {
    const areaTxt = formatLtAreaCell(row.area);
    const balTxt = formatLtAreaCell(row.balcony);
    const areaCell = areaTxt === '—' ? '—' : `${areaTxt} m²`;
    const balCell = balTxt === '—' ? '—' : `${balTxt} m²`;
    const badge = `<span class="${escapeHtml(statusBadgeClass(row.status))}">${escapeHtml(row.status ?? '')}</span>`;
    const label = escapeHtml(rowAriaLabel(row));
    const cells = `
      <span class="apartments-table__cell">${escapeHtml(row.house)}</span>
      <span class="apartments-table__cell">${escapeHtml(row.apartment)}</span>
      <span class="apartments-table__cell">${escapeHtml(String(row.floor))}</span>
      <span class="apartments-table__cell">${escapeHtml(String(row.room ?? ''))}</span>
      <span class="apartments-table__cell">${escapeHtml(areaCell)}</span>
      <span class="apartments-table__cell">${escapeHtml(balCell)}</span>
      <span class="apartments-table__cell apartments-table__cell--features">${escapeHtml(row.features ?? '')}</span>
      <span class="apartments-table__cell apartments-table__cell--status">${badge}</span>`;

    const wrap = document.createElement('div');
    wrap.className = 'apartments-table__row-container';

    if (row.link !== '') {
      wrap.innerHTML = `
        <a href="${escapeHtml(row.link)}" class="apartments-table__row-hit block w-full text-inherit no-underline outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg" aria-label="${label}">
          <div class="${APARTMENTS_TABLE_INNER}">
            <div class="apartments-table__row apartments-table__row--link">${cells}</div>
          </div>
        </a>`;
    } else {
      wrap.innerHTML = `
        <div class="${APARTMENTS_TABLE_INNER}">
          <div class="apartments-table__row apartments-table__row--static">${cells}</div>
        </div>`;
    }
    frag.appendChild(wrap);
  }
  tbody.innerHTML = '';
  tbody.appendChild(frag);
}

async function fetchApartments(cfg: MigdolaiApartmentsCfg, sliders: { floor?: NoUiApi; room?: NoUiApi; area?: NoUiApi }): Promise<void> {
  const tbody = qs<HTMLElement>('#apartments-table-body');
  if (tbody) {
    tbody.innerHTML = `
      <div class="apartments-table__row-container apartments-table__row-container--message apartments-table__loading">
        <div class="${APARTMENTS_TABLE_INNER}">
          <p class="apartments-table__row apartments-table__row--message" role="status">${escapeHtml(cfg.i18n.loading ?? '…')}</p>
        </div>
      </div>`;
  }

  try {
    const res = await fetch(cfg.ajaxUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: collectPayload(cfg, sliders).toString(),
      credentials: 'same-origin',
    });
    const json = (await res.json()) as { success?: boolean; data?: ApartmentRow[] };
    if (!json.success || !Array.isArray(json.data)) {
      throw new Error('bad response');
    }
    renderRows(cfg, json.data);
  } catch {
    const tb = qs<HTMLElement>('#apartments-table-body');
    if (tb) {
      tb.innerHTML = `
        <div class="apartments-table__row-container apartments-table__row-container--message apartments-table__empty">
          <div class="${APARTMENTS_TABLE_INNER}">
            <p class="apartments-table__row apartments-table__row--message">${escapeHtml(cfg.i18n.error ?? '')}</p>
          </div>
        </div>`;
    }
  }
}

/** noUiSlider requires max > min; API uses array values for range keys. */
function safeSpan(min: number, max: number): [number, number] {
  const lo = Math.round(min);
  const hi = Math.round(max);
  if (hi <= lo) {
    return [lo, lo + 1];
  }
  return [lo, hi];
}

type SliderFormat = 'int' | 'area';

function sliderToLabel(kind: SliderFormat, value: number): string {
  if (kind === 'area') {
    return formatLtNumber(value);
  }
  return String(Math.round(value));
}

function createRangeSlider(
  el: HTMLElement | null,
  min: number,
  max: number,
  step: number,
  valueFormat: SliderFormat,
  initial?: [number, number] | null,
): NoUiApi | undefined {
  const factory = window.noUiSlider;
  if (!el || !factory) {
    return undefined;
  }
  const [lo, hi] = safeSpan(min, max);
  const start: [number, number] = initial
    ? [Math.max(lo, Math.min(initial[0], hi)), Math.max(lo, Math.min(initial[1], hi))]
    : [lo, hi];
  try {
    return factory.create(el, {
      start,
      connect: true,
      step,
      range: { min: [lo], max: [hi] },
      behaviour: 'tap-drag',
      animate: true,
      animationDuration: 200,
      tooltips: true,
      format: {
        to: (v: number) => sliderToLabel(valueFormat, v),
        from: Number,
      },
    } as Record<string, unknown>) as NoUiApi;
  } catch (err) {
    console.error('[apartments] noUiSlider.create failed', err);
    return undefined;
  }
}

function initApartments(): void {
  const cfg = readCfg();
  if (!cfg || !window.noUiSlider) {
    return;
  }

  if (cfg.forcedPatalpuType === 'komercine') {
    const typeSel = qs<HTMLSelectElement>('#apartments-type');
    if (typeSel) {
      typeSel.value = 'komercine';
      typeSel.disabled = true;
    }
  }

  const b = cfg.bounds;
  const floorMin = Math.round(b.floor_min);
  const floorMax = Math.round(b.floor_max);
  const roomMin = Math.round(b.room_min);
  const roomMax = Math.round(b.room_max);
  const areaMin = Math.floor(b.area_min);
  const areaMax = Math.ceil(b.area_max);

  const sliders: { floor?: NoUiApi; room?: NoUiApi; area?: NoUiApi } = {};

  const run = debounce(() => {
    void fetchApartments(cfg, sliders);
  }, 220);

  const floorEl = qs<HTMLElement>('#apartments-slider-floor');
  sliders.floor = createRangeSlider(floorEl, floorMin, floorMax, 1, 'int');
  sliders.floor?.on('update', run);

  const roomEl = qs<HTMLElement>('#apartments-slider-room');
  sliders.room = createRangeSlider(roomEl, roomMin, roomMax, 1, 'int', cfg.presetRooms);
  sliders.room?.on('update', run);

  const areaEl = qs<HTMLElement>('#apartments-slider-area');
  sliders.area = createRangeSlider(areaEl, areaMin, areaMax, 1, 'area');
  sliders.area?.on('update', run);

  qs('#apartments-filter-form')?.addEventListener('change', run);

  void fetchApartments(cfg, sliders);

  if (cfg.presetRooms) {
    scrollToQuickSearch();
  }
}

/** Preset URLs land directly on the quick search zone (after load, so layout/animations have settled). */
function scrollToQuickSearch(): void {
  const go = () => {
    const target = qs<HTMLElement>('.apartments-filter');
    if (target) {
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 120, behavior: 'instant' });
    }
  };
  if (document.readyState === 'complete') {
    setTimeout(go, 100);
  } else {
    window.addEventListener('load', () => setTimeout(go, 100), { once: true });
  }
}

ready(initApartments);
